import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
const budgetData = [{
  name: 'Injectables',
  monthly: 7500,
  daily: 250,
  percent: 37.5,
  color: '#1B2A4A'
}, {
  name: 'Body & Weight Loss',
  monthly: 5000,
  daily: 167,
  percent: 25,
  color: '#2A3F6A'
}, {
  name: 'Laser & Skin',
  monthly: 4000,
  daily: 133,
  percent: 20,
  color: '#C9A96E'
}, {
  name: 'Medical & Wellness',
  monthly: 2500,
  daily: 83,
  percent: 12.5,
  color: '#8B7355'
}, {
  name: 'Brand Defense',
  monthly: 1000,
  daily: 33,
  percent: 5,
  color: '#A8B4C4'
}];
export function BudgetAllocation() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-white rounded-xl shadow-xl p-4 border border-[#1B2A4A]/10" data-id="element-10">
          <p className="font-semibold text-[#1B2A4A] mb-1" data-id="element-11">{data.name}</p>
          <p className="text-sm text-[#666]" data-id="element-12">
            ${data.monthly.toLocaleString()}/month
          </p>
          <p className="text-sm text-[#C9A96E] font-medium" data-id="element-13">
            {data.percent}% of budget
          </p>
        </div>;
    }
    return null;
  };
  return <section id="budget" className="py-24 md:py-32 bg-[#F8F9FA]" ref={ref} data-id="element-14">
      <div className="max-w-6xl mx-auto px-6" data-id="element-15">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-16">
          <p className="text-[#C9A96E] text-sm font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-17">
            Budget
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#1B2A4A]" data-id="element-18">
            Budget Allocation
          </h2>
          <p className="text-[#666] mt-4 max-w-2xl mx-auto" data-id="element-19">
            We split the budget across 5 focused campaigns, weighted toward your
            highest-revenue services.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-20">
          {/* Donut Chart */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-21">
            <div className="aspect-square max-w-md mx-auto" data-id="element-22">
              <ResponsiveContainer width="100%" height="100%" data-id="element-23">
                <PieChart data-id="element-24">
                  <Pie data={budgetData} cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" paddingAngle={3} dataKey="monthly" onMouseEnter={(_, index) => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} animationBegin={0} animationDuration={1000} data-id="element-25">
                    {budgetData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} stroke="none" style={{
                    transform: activeIndex === index ? 'scale(1.05)' : 'scale(1)',
                    transformOrigin: 'center',
                    transition: 'transform 0.2s ease-out'
                  }} data-id="element-26" />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip data-id="element-28" />} data-id="element-27" />
                </PieChart>
              </ResponsiveContainer>
              {/* Center text */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-29">
                <div className="text-center" data-id="element-30">
                  <p className="text-4xl font-light text-[#1B2A4A]" data-id="element-31">$20K</p>
                  <p className="text-sm text-[#666]" data-id="element-32">Monthly Total</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Budget Table */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-33">
            <div className="bg-white rounded-2xl shadow-sm border border-[#1B2A4A]/5 overflow-hidden" data-id="element-34">
              <div className="grid grid-cols-4 gap-4 p-4 bg-[#1B2A4A] text-white text-sm font-medium" data-id="element-35">
                <div data-id="element-36">Campaign</div>
                <div className="text-right" data-id="element-37">Monthly</div>
                <div className="text-right" data-id="element-38">Daily</div>
                <div className="text-right" data-id="element-39">%</div>
              </div>
              {budgetData.map((item, index) => <motion.div key={item.name} initial={{
              opacity: 0,
              x: 20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.4,
              delay: 0.4 + index * 0.1
            }} className={`grid grid-cols-4 gap-4 p-4 items-center ${index !== budgetData.length - 1 ? 'border-b border-[#1B2A4A]/5' : ''}`} onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} style={{
              backgroundColor: activeIndex === index ? `${item.color}10` : 'transparent'
            }} data-id="element-40">
                  <div className="flex items-center gap-3" data-id="element-41">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                  backgroundColor: item.color
                }} data-id="element-42" />
                    <span className="text-sm font-medium text-[#1B2A4A]" data-id="element-43">
                      {item.name}
                    </span>
                  </div>
                  <div className="text-right text-sm text-[#333]" data-id="element-44">
                    ${item.monthly.toLocaleString()}
                  </div>
                  <div className="text-right text-sm text-[#666]" data-id="element-45">
                    ${item.daily}
                  </div>
                  <div className="text-right text-sm font-semibold text-[#C9A96E]" data-id="element-46">
                    {item.percent}%
                  </div>
                </motion.div>)}
              <div className="grid grid-cols-4 gap-4 p-4 bg-[#F8F9FA] font-semibold" data-id="element-47">
                <div className="text-[#1B2A4A]" data-id="element-48">Total</div>
                <div className="text-right text-[#1B2A4A]" data-id="element-49">$20,000</div>
                <div className="text-right text-[#666]" data-id="element-50">$667</div>
                <div className="text-right text-[#C9A96E]" data-id="element-51">100%</div>
              </div>
            </div>

            <motion.p initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="mt-6 text-sm text-[#666] leading-relaxed" data-id="element-52">
              <span className="text-[#1B2A4A] font-medium" data-id="element-53">
                Injectables and weight loss
              </span>{' '}
              get the largest share because they drive the highest search volume
              and revenue per patient. Each campaign has its own budget, so we
              can increase or decrease spend on any service category based on
              real results.
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>;
}